import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useBi, useTranslation } from '@wix/yoshi-flow-editor';

import { groupsSearchSearchResultSrc111Evid523 } from '@wix/bi-logger-groups-data/v2';

import { selectGroupsStatus } from 'store/selectors';
import { EGroupsNamespace } from 'store/groups/constants';

import { useController } from 'common/context/controller';
import { EGroupPartition } from '@wix/social-groups-serverless/dist/groups/types';

import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';

import {
  GroupsResultsList,
  GroupsResultsListSkeleton,
} from './GroupsResultsList';

interface IProps {
  query?: string;

  container: HTMLElement | null;
  onReset(): void;
  origin: string;
  'data-hook'?: string;
}

export function GroupsSearchResults({
  query,
  onReset,
  container,
  origin,
  'data-hook': dataHook,
}: IProps) {
  const { groups$ } = useController();
  const { t } = useTranslation();
  const bi = useBi();

  const { fetch } = useSelector(selectGroupsStatus(EGroupsNamespace.SEARCH));

  useEffect(() => {
    if (query) {
      searchGroups();
    }
  }, [query]);

  useEffect(() => {
    if (fetch?.error) {
      bi.report(
        groupsSearchSearchResultSrc111Evid523({
          origin,
          inputText: query,
          responseStatus: 'error',
          tabName: 'groups',
        }),
      );
    }
  }, [fetch?.error]);

  if (!query) {
    return (
      <EmptyState
        bw
        variant="section"
        data-hook={dataHook}
        data-status="empty"
        title={t('groups-web.groups-search.empty-state.title')}
        subtitle={t('groups-web.groups-search.empty-state.subtitle')}
        role="status"
        aria-live="polite"
      />
    );
  }

  if (fetch?.loading) {
    return (
      <GroupsResultsListSkeleton
        data-hook={dataHook}
        height="100%"
        data-status="loading"
      />
    );
  }

  if (fetch?.error) {
    return (
      <ErrorState
        bw
        variant="section"
        data-hook={dataHook}
        data-status="fetch-error"
        onRetry={handleRetry}
        buttonProps={{ variant: 'basic', outlined: true }}
        title={t('groups-web.group-search.error-state.title')}
        subtitle={t('groups-web.group-search.error-state.subtitle')}
        actionLabel={t('groups-web.group-search.error-state.retry')}
        role="alert"
        aria-live="assertive"
      />
    );
  }

  return (
    <GroupsResultsList
      query={query}
      data-status="success"
      data-hook="groups-results-list"
      onReset={onReset}
      container={container}
      origin={origin}
    />
  );

  function handleRetry() {
    searchGroups();
  }

  function searchGroups() {
    groups$.query({
      partition: EGroupPartition.ALL,
      namespace: EGroupsNamespace.SEARCH,
      title: query,
      limit: 15,
    });
  }
}

GroupsSearchResults.displayName = 'GroupsSearchResults';
